import { useCallback } from 'react';
import { useSolutionOrg } from '../../config/useSolutionOrg';
import { useLogo } from '../../config/useLogo';
import { getServiceNationSsoToken } from '../../api/loginAPI';
import { HOME_ROUTE } from '../../routes/namedRoutes';

const EMBEDDED_PARAM = 'embedded';

// Only the Community and its known Circle aliases may supply deep-link paths.
const COMMUNITY_ORIGINS = new Set([
  'https://connect.servicenation.com',
  'https://servicenation.circle.so',
  'https://sn-community-d45ab7.circle.so',
]);
const MAX_COMMUNITY_TARGET_LENGTH = 2048;

function getCommunityTarget(communityUrl: string): string | undefined {
  try {
    const target = new URL(communityUrl);
    const path = target.pathname + target.search;
    // Check encoded separators too, in case the redirect handler decodes the path.
    const decodedPath = decodeURIComponent(target.pathname);
    if (!COMMUNITY_ORIGINS.has(target.origin) || path.length > MAX_COMMUNITY_TARGET_LENGTH ||
      decodedPath.startsWith('//') || new URL(decodedPath, target.origin).origin !== target.origin) {
      return undefined;
    }
    return path;
  } catch {
    return undefined;
  }
}

export const useServiceNationUrl = () => {
  const { baseUrl, solutionOrgSpecificAppName } = useSolutionOrg();
  const { secondaryOne: logoUrl } = useLogo();

  // `destinationParams` is either a destination alias from SN's config/edge.php (optionally with a
  // Community deep link) or a full SN URL (SN-1167), whose path and query string are kept.
  // `isEmbedded` marks URLs that are shown in an iframe rather than opened in a new tab
  const buildEdgeAuthUrl = useCallback(async (
    destinationParams: { destination: string; communityUrl?: string } | { destinationUrl: string },
    isEmbedded?: boolean,
  ) => {
    const { token } = await getServiceNationSsoToken();
    const serviceNationBaseUrl = process.env.REACT_APP_SERVICE_NATION_URL ||
      'https://app.servicenation.dev';
    const homeBase = (baseUrl as string) || window.location.origin;
    const logo = (logoUrl as string) || 'https://d2o7bp9oz3gksv.cloudfront.net/login_page_logo_sn_by_ever_Pro_29fb73bc64.png';

    const url = new URL('/edge-auth', serviceNationBaseUrl);
    url.searchParams.set('token', token);
    if ('destinationUrl' in destinationParams) {
      url.searchParams.set('destinationUrl', destinationParams.destinationUrl);
    } else {
      url.searchParams.set('destination', destinationParams.destination);
    }
    url.searchParams.set('home', new URL(HOME_ROUTE, homeBase).toString());
    url.searchParams.set('logo', logo);
    url.searchParams.set('brand', (solutionOrgSpecificAppName as string) || 'Service Nation');
    if (isEmbedded) url.searchParams.set(EMBEDDED_PARAM, 'true');
    if ('destination' in destinationParams && destinationParams.destination === 'community' &&
      destinationParams.communityUrl) {
      // SN expects the validated path and query, encoded as one parameter so an
      // event's own query survives SSO (SN-1139). Invalid targets open Community home.
      const target = getCommunityTarget(destinationParams.communityUrl);
      if (target) url.searchParams.set('communityTarget', target);
    }

    return url.toString();
  }, [baseUrl, logoUrl, solutionOrgSpecificAppName]);

  const buildServiceNationUrl = useCallback((
    destination: string,
    isEmbedded?: boolean,
    communityUrl?: string,
  ) => buildEdgeAuthUrl({ destination, communityUrl }, isEmbedded), [buildEdgeAuthUrl]);

  // Any full Service Nation URL; its path and query string are kept (SN-1167)
  const buildServiceNationUrlFromLink = useCallback(
    (destinationUrl: string) => buildEdgeAuthUrl({ destinationUrl }),
    [buildEdgeAuthUrl],
  );

  return { buildServiceNationUrl, buildServiceNationUrlFromLink };
};
